/* Assorted inventory arithmetic and cart orchestration. Shopify remains the stock authority. */
(function (root) {
  'use strict';
  class AllocationError extends Error {
    constructor(code, message) { super(message); this.name = 'AllocationError'; this.code = code; }
  }
  const fail = (code, message) => { throw new AllocationError(code, message); };
  const integer = (value, min = 0) => {
    if (!Number.isSafeInteger(value) || value < min) fail('INVALID_DATA', 'We could not verify this quantity. Please reload and try again.');
    return value;
  };
  const id = (value) => {
    if (typeof value === 'number') integer(value, 1);
    const result = String(value);
    if (!/^[1-9]\d*$/.test(result)) fail('INVALID_DATA', 'We could not verify this item. Please reload your cart.');
    return result;
  };
  const sum = (a, b) => integer(a + b);
  const numericOrder = (a, b) => BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0;

  function family(snapshot) {
    if (!snapshot || snapshot.schema !== 1 || snapshot.enabled !== true || !Array.isArray(snapshot.variants)) {
      fail('UNAVAILABLE', 'This assortment is temporarily unavailable. Please try again later.');
    }
    const productId = id(snapshot.productId);
    if (snapshot.variants.length !== snapshot.variantCount || snapshot.variantCount < 2 || snapshot.variantCount > 100) {
      fail('INVALID_DATA', 'We could not verify every style in this assortment. Please try again later.');
    }
    const seen = new Set();
    const skus = new Set();
    let standard;
    const variants = snapshot.variants.map((variant) => {
      const variantId = id(variant.id);
      if (seen.has(variantId) || variant.inventoryTracked !== true || variant.inventoryPolicy !== 'deny' ||
          typeof variant.available !== 'boolean' || typeof variant.retired !== 'boolean' ||
          !variant.sku || skus.has(variant.sku) || variant.quantityMin !== 1 || variant.quantityIncrement !== 1 ||
          variant.quantityMax !== null || variant.sellingPlanRequired || variant.quantityPriceBreaks) {
        fail('INVALID_CONFIGURATION', 'This assortment is temporarily unavailable. Please contact us for help.');
      }
      seen.add(variantId); skus.add(variant.sku);
      integer(variant.availableQuantity, -Number.MAX_SAFE_INTEGER);
      integer(variant.rank);
      integer(variant.price);
      const commercial = JSON.stringify([variant.price, variant.taxable, variant.requiresShipping]);
      if (!variant.retired) {
        if (standard !== undefined && commercial !== standard) fail('INVALID_CONFIGURATION', 'This assortment is temporarily unavailable. Please contact us for help.');
        standard = commercial;
      } else if (variant.availableQuantity > 0) {
        fail('INVALID_CONFIGURATION', 'This assortment is temporarily unavailable. Please contact us for help.');
      }
      return { ...variant, id: variantId, capacity: variant.available && !variant.retired ? Math.max(0, variant.availableQuantity) : 0 };
    }).sort((a, b) => a.rank - b.rank || numericOrder(a.id, b.id));
    if (standard === undefined) fail('INVALID_CONFIGURATION', 'This assortment is temporarily unavailable.');
    return { ...snapshot, productId, variants };
  }

  function cartLines(cart) {
    if (!cart || !Array.isArray(cart.items)) fail('INVALID_CART', 'We could not read your cart. Please reload it before continuing.');
    return cart.items.map((line) => ({ ...line, variant_id: id(line.variant_id), product_id: id(line.product_id), quantity: integer(line.quantity) }));
  }

  function quantities(lines) {
    const totals = new Map();
    for (const line of lines) totals.set(line.variant_id, sum(totals.get(line.variant_id) || 0, line.quantity));
    return totals;
  }

  function validateCart(snapshot, cart) {
    const stock = family(snapshot);
    const lines = cartLines(cart);
    const members = new Map(stock.variants.map((variant) => [variant.id, variant]));
    const totals = quantities(lines);
    for (const line of lines.filter((item) => item.product_id === stock.productId)) {
      if (!members.has(line.variant_id) || line.selling_plan_allocation || line.parent_relationship) {
        fail('STALE_CART', 'An item in your cart has changed. Remove it and add it again from the product page.');
      }
    }
    for (const variant of stock.variants) {
      if ((totals.get(variant.id) || 0) > variant.capacity) {
        fail('STOCK_CHANGED', 'Stock has changed for an assigned style. Please reduce or remove that cart item before continuing.');
      }
    }
    return { stock, lines, totals };
  }

  function allocate(snapshot, cart, requestedQuantity) {
    integer(requestedQuantity, 1);
    const { stock, totals } = validateCart(snapshot, cart);
    let remaining = requestedQuantity;
    const items = [];
    for (const variant of stock.variants) {
      const quantity = Math.min(remaining, variant.capacity - (totals.get(variant.id) || 0));
      if (quantity > 0) { items.push({ id: variant.id, quantity }); remaining -= quantity; }
      if (remaining === 0) break;
    }
    if (remaining) fail('INSUFFICIENT_STOCK', 'There are not enough items available for that quantity. Please choose a smaller quantity.');
    return items;
  }

  function validateLineChange(snapshot, cart, key, quantity) {
    integer(quantity);
    const lines = cartLines(cart);
    const line = lines.find((item) => item.key === key);
    if (!line) fail('STALE_CART', 'Your cart has changed. Please reload it before editing this item.');
    // Reductions/removal must remain possible even when stock or configuration changed.
    if (quantity <= line.quantity) return line;
    const { stock } = validateCart(snapshot, cart);
    if (line.product_id !== stock.productId) fail('INVALID_CART', 'Your cart has changed. Please reload it.');
    const variant = stock.variants.find((item) => item.id === line.variant_id);
    const others = (quantities(lines).get(line.variant_id) || 0) - line.quantity;
    if (!variant || quantity > variant.capacity - others) {
      fail('INSUFFICIENT_STOCK', 'There are not enough of this assigned style. To add other available styles, return to the product page.');
    }
    return line;
  }

  function createQueue() {
    let tail = Promise.resolve();
    let pending = 0;
    return {
      run(task) {
        pending++;
        const result = tail.then(task);
        tail = result.catch(() => {}).finally(() => { pending--; });
        return result;
      },
      get pending() { return pending; },
      idle() { return tail; },
    };
  }

  function createClient(api) {
    const recover = async (error) => {
      let cart = null;
      try { cart = await api.getCart(); } catch (_) { /* Unknown outcome: never retry a write. */ }
      const result = new AllocationError('REVIEW_CART', 'We could not confirm the full update. Please review your cart before trying again.');
      result.cart = cart; result.cause = error; result.needsReview = true;
      api.requireReview?.();
      throw result;
    };
    return {
      async add(url, requestedQuantity, onPlan) {
        const cart = await api.getCart();
        const snapshot = await api.getFamily(url);
        const items = allocate(snapshot, cart, requestedQuantity);
        onPlan?.(items);
        try {
          await api.add(items);
          const updated = await api.getCart();
          const before = quantities(cartLines(cart));
          const after = quantities(cartLines(updated));
          const intended = new Map(items.map((item) => [item.id, item.quantity]));
          for (const variant of family(snapshot).variants) {
            if ((after.get(variant.id) || 0) - (before.get(variant.id) || 0) !== (intended.get(variant.id) || 0)) {
              fail('CART_CHANGED', 'Your cart changed during this update.');
            }
          }
          return { cart: updated, items };
        } catch (error) { return recover(error); }
      },
      async change(url, key, quantity) {
        const cart = await api.getCart();
        const existing = cartLines(cart).find((item) => item.key === key);
        if (!existing) fail('STALE_CART', 'Your cart has changed. Please reload it.');
        const snapshot = quantity > existing.quantity ? await api.getFamily(url) : null;
        validateLineChange(snapshot, cart, key, quantity);
        try {
          await api.change(key, quantity);
          const updated = await api.getCart();
          const actual = cartLines(updated).find((item) => item.key === key);
          if ((actual?.quantity || 0) !== quantity) fail('CART_CHANGED', 'Your cart changed during this update.');
          return updated;
        } catch (error) { return recover(error); }
      },
      async checkout() {
        const cart = await api.getCart();
        const handles = [...new Set(cartLines(cart).map((line) => line.handle).filter((handle) => api.handles.includes(handle)))];
        for (const handle of handles) {
          const snapshot = await api.getFamily(api.productUrl(handle));
          if (snapshot.enabled) validateCart(snapshot, cart);
        }
        return cart;
      },
    };
  }
  root.BayoAssortedCore = { AllocationError, family, allocate, validateCart, validateLineChange, createQueue, createClient };
})(globalThis);
